import { useState } from "react";
import { cn } from "@/lib/utils";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { tFormat } from "@/lib/i18n/t-format";
import type { Translations } from "@/lib/i18n/types";

const ACCEPTED_CONTEXT_TYPES = [".txt", ".md", ".json", ".yaml", ".yml", ".csv", ".xml", ".html"];

interface ContextFileDropZoneProps {
  onFilesAdded: (files: { name: string; content: string }[]) => void;
  t: Translations;
}

export function ContextFileDropZone({ onFilesAdded, t }: ContextFileDropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const processFiles = async (fileList: File[]) => {
    const valid = fileList.filter(f => {
      const ext = "." + (f.name.split(".").pop()?.toLowerCase() || "");
      return ACCEPTED_CONTEXT_TYPES.includes(ext);
    });
    if (valid.length === 0) {
      toast.error(tFormat(t.doc_no_supported_files, { types: ACCEPTED_CONTEXT_TYPES.join(", ") }));
      return;
    }
    const results: { name: string; content: string }[] = [];
    for (const file of valid) {
      if (file.size > 2 * 1024 * 1024) { toast.error(`${file.name} is too large (max 2MB)`); continue; }
      try {
        const text = await file.text();
        results.push({ name: file.name, content: text.substring(0, 50000) });
      } catch { toast.error(`Could not read ${file.name}`); }
    }
    if (results.length > 0) onFilesAdded(results);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
      onDrop={(e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
        processFiles(Array.from(e.dataTransfer.files));
      }}
      className={cn(
        "border-2 border-dashed rounded-lg p-3 text-center transition-all cursor-pointer",
        isDragOver ? "border-primary bg-primary/5" : "border-border/60 hover:border-border"
      )}
    >
      <div className="flex flex-col items-center gap-1">
        <Upload className="w-4 h-4 text-muted-foreground/60" />
        <span className="text-[10px] text-muted-foreground">{t.idx_drop_files}</span>
        <span className="text-[10px] text-muted-foreground/50">.txt .md .json .yaml .csv .xml .html</span>
      </div>
    </div>
  );
}
